import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';

export const CGV = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  return (
    <>
      <SEOHead
        title={`${isEnglish ? 'Terms of Sale' : 'CGV'} - WiseRank.ai`}
        description={isEnglish 
          ? "Terms and conditions of sale for WiseRank.ai services"
          : "Conditions générales de vente des services WiseRank.ai"}
        path="/cgv"
      />

      <article className="min-h-screen pt-24 pb-12">
        <section className="section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent premium-gradient">
              {isEnglish ? "Terms and Conditions of Sale" : "Conditions Générales de Vente"}
            </h1>

            <div className="prose dark:prose-invert max-w-none">
              {isEnglish ? (
                // English Version
                <>
                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">1. Purpose</h2>
                    <p>
                      These Terms and Conditions of Sale (T&Cs) define the rights and obligations of the parties in the context of using the CraftSEO.ai platform and its associated services.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">2. Services Offered</h2>
                    <p>
                      CraftSEO.ai offers an SEO-optimized content generation tool, accessible through à la carte packages or monthly subscriptions. These services include text creation, images, audio, as well as human proofreading options.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">3. Order and Service Access Methods</h2>
                    <p>
                      Orders are placed exclusively online on the CraftSEO.ai platform. Once payment is validated, the user gains access to credits corresponding to the chosen package.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">4. Pricing and Payment</h2>
                    <h3 className="text-xl font-semibold mb-3">4.1. Pricing</h3>
                    <p>
                      Applicable rates are those displayed on CraftSEO.ai at the time of order. They are indicated in euros excluding taxes (HT).
                    </p>

                    <h3 className="text-xl font-semibold mb-3 mt-6">4.2. Payment</h3>
                    <p>
                      Full payment is required at the time of order and is made through payment methods available on the platform.
                    </p>

                    <h3 className="text-xl font-semibold mb-3 mt-6">4.3. Non-refundable Payments</h3>
                    <p>
                      All payments made are final and non-refundable, except in cases of serious fault attributable to CraftSEO.ai preventing service delivery. This clause is essential to ensure the viability of our services. By accepting these T&Cs, the user expressly waives any refund once payment is validated. For any questions or exceptional requests, you can contact our customer service at hello@craftseo.ai.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">5. Right of Withdrawal</h2>
                    <p>
                      In accordance with Article L. 221-28 of the Consumer Code, the right of withdrawal does not apply to digital content delivered immediately after purchase and whose execution has begun with the express agreement of the user. By validating their order, the user acknowledges waiving their right of withdrawal for services delivered by CraftSEO.ai.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">6. User Responsibility</h2>
                    <p>
                      The user is responsible for the use of content generated via the platform and agrees to comply with applicable laws and regulations. CraftSEO.ai cannot be held responsible for consequences related to misuse of services.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">7. Intellectual Property</h2>
                    <p>
                      All content generated via CraftSEO.ai is transferred to the user, who becomes its exclusive owner. However, CraftSEO.ai retains rights to the algorithms, methods, and tools used. CraftSEO.ai assumes no guarantee regarding the originality or absence of third-party rights violations in generated content.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">8. Termination and Suspension</h2>
                    <p>
                      CraftSEO.ai reserves the right to suspend or terminate a user's access in case of non-compliance with these T&Cs.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">9. Dispute Resolution and Mediation</h2>
                    <p>
                      In case of dispute, an amicable solution will be sought before any legal action. The user may also resort to a consumer mediator in accordance with Article L. 612-1 of the Consumer Code. The competent courts will be those of CraftSEO's jurisdiction in case of failed mediation.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">10. Applicable Law</h2>
                    <p>
                      These T&Cs are governed by French law. In case of dispute, the competent courts will be those of CraftSEO's jurisdiction.
                    </p>
                  </section>
                </>
              ) : (
                // French Version
                <>
                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">1. Objet</h2>
                    <p>
                      Les présentes Conditions Générales de Vente (CGV) ont pour objet de définir les droits et obligations des parties dans le cadre de l'utilisation de la plateforme CraftSEO.ai et de la fourniture des services associés.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">2. Services Proposés</h2>
                    <p>
                      CraftSEO.ai propose un outil de génération de contenu optimisé pour le référencement (SEO), accessible via des forfaits à la carte ou des abonnements mensuels. Ces services incluent la création de textes, d'images, d'audios, ainsi que des options de relecture humaine.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">3. Modalités de Commande et d'Accès aux Services</h2>
                    <p>
                      Les commandes sont effectuées exclusivement en ligne sur la plateforme CraftSEO.ai. Une fois le paiement validé, l'utilisateur accède aux crédits correspondant au forfait choisi.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">4. Tarifs et Paiement</h2>
                    <h3 className="text-xl font-semibold mb-3">4.1. Tarifs</h3>
                    <p>
                      Les tarifs applicables sont ceux affichés sur le site CraftSEO.ai au moment de la commande. Ils sont indiqués en euros hors taxes (HT).
                    </p>

                    <h3 className="text-xl font-semibold mb-3 mt-6">4.2. Paiement</h3>
                    <p>
                      Le paiement est exigible en totalité à la commande et s'effectue par les moyens de paiement disponibles sur la plateforme.
                    </p>

                    <h3 className="text-xl font-semibold mb-3 mt-6">4.3. Non-remboursement des Paiements</h3>
                    <p>
                      Tous les paiements effectués sont définitifs et non remboursables, sauf en cas de défaut grave imputable à CraftSEO.ai empêchant la fourniture des services. Cette clause est essentielle pour garantir la viabilité de nos services. En acceptant ces CGV, l'utilisateur renonce expressément à tout remboursement une fois le paiement validé. Pour toute question ou demande exceptionnelle, vous pouvez contacter notre service client à l'adresse hello@craftseo.ai.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">5. Droit de Rétractation</h2>
                    <p>
                      Conformément à l'article L. 221-28 du Code de la consommation, le droit de rétractation ne s'applique pas à : Les contenus numériques fournis immédiatement après l'achat et dont l'exécution a commencé avec l'accord exprès de l'utilisateur. En validant sa commande, l'utilisateur reconnaît renoncer à son droit de rétractation pour les services délivrés par CraftSEO.ai.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">6. Responsabilité de l'Utilisateur</h2>
                    <p>
                      L'utilisateur est responsable de l'utilisation des contenus générés via la plateforme et s'engage à respecter les lois et réglementations en vigueur. CraftSEO.ai ne pourra être tenu responsable des conséquences liées à une mauvaise utilisation des services.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">7. Propriété Intellectuelle</h2>
                    <p>
                      Tous les contenus générés via CraftSEO.ai sont cédés à l'utilisateur, qui en devient le propriétaire exclusif. Toutefois, CraftSEO.ai conserve les droits sur les algorithmes, méthodes, et outils utilisés. CraftSEO.ai n'assume aucune garantie quant à l'originalité ou à l'absence de violations des droits de tiers dans les contenus générés.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">8. Résiliation et Suspension</h2>
                    <p>
                      CraftSEO.ai se réserve le droit de suspendre ou résilier l'accès d'un utilisateur en cas de non-respect des présentes CGV.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">9. Résolution des Litiges et Médiation</h2>
                    <p>
                      En cas de litige, une solution amiable sera recherchée avant toute action judiciaire. L'utilisateur peut également recourir à un médiateur de la consommation conformément à l'article L. 612-1 du Code de la consommation. Les tribunaux compétents seront ceux du ressort de l'entreprise CraftSEO en cas d'échec de la médiation.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">10. Loi Applicable</h2>
                    <p>
                      Les présentes CGV sont régies par le droit français. En cas de litige, les tribunaux compétents seront ceux du ressort de l'entreprise CraftSEO.
                    </p>
                  </section>
                </>
              )}
            </div>
          </motion.div>
        </section>
      </article>
    </>
  );
};